import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import DashboardHeader, {
    AutoRefreshIndicator,
    AutoRefreshLabel,
} from '~/components/logistics/fulfillmentLifecycle/components/DashboardHeader';
import FilterControls from '~/components/logistics/fulfillmentLifecycle/components/FilterControls';
import RouteCardSkeleton from '~/components/logistics/fulfillmentLifecycle/components/RouteCardSkeleton';
import RoutesGrid from '~/components/logistics/fulfillmentLifecycle/components/RoutesGrid';
import SummaryAlerts from '~/components/logistics/fulfillmentLifecycle/components/SummaryAlerts';
import { useFulfillmentLifecycleData } from '~/components/logistics/fulfillmentLifecycle/hooks/useFulfillmentLifecycleData';
import { useInterval } from '~/components/logistics/fulfillmentLifecycle/hooks/useInterval';
import { UserContext } from '~/contexts/UserContext';
import { Alert, AlertDescription, AlertTitle } from '~/shadcn/components/ui/Alert';
import { Skeleton } from '~/shadcn/components/ui/Skeleton';
import dayjs from '~/utils/dayjsConfig';

/** Auto-refresh interval in milliseconds (15 seconds) */
const REFRESH_INTERVAL_MS = 15000;

/**
 * FulfillmentLifecycleDashboard
 *
 * Main container component for the Fulfillment Lifecycle dashboard.
 * Displays route status, risk indicators, and throughput analytics.
 * Auto-refreshes every 15 seconds to keep data current.
 *
 * State:
 * - selectedDate: Operating date for the dashboard (default: today)
 * - filters: Active filter selections (deliveryTypes, orderSources, statusFilter, milestone)
 * - autoRefreshEnabled: Whether auto-refresh is active (default: true)
 *
 * Data fetching handled by useFulfillmentLifecycleData hook.
 */
function FulfillmentLifecycleDashboard() {
    const { getFacilityID, getFacilityName } = useContext(UserContext);
    const facilityId = getFacilityID();
    const facilityName = getFacilityName();

    // State for date selection
    const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));

    // State for filters
    const [filters, setFilters] = useState({
        orderSources: [],
        deliveryTypes: [],
        statusFilter: 'all', // 'all' | 'critical' | 'atRisk' | 'onTrack'
        milestone: 'all', // 'all' | 'labels' | 'packing' | 'loading' | 'complete'
    });

    // Auto-refresh state
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

    // Fetch dashboard data (API filters: orderSources + deliveryTypes)
    const { error, loading, refetch, routes, summary } = useFulfillmentLifecycleData(facilityId, selectedDate, {
        orderSources: filters.orderSources,
        deliveryTypes: filters.deliveryTypes,
    });

    // Track whether we have cached data to distinguish initial load from refresh errors
    const hasCachedData = routes.length > 0;

    // Track whether the initial fetch has completed (for auto-refresh and toast gating)
    const hasLoadedOnceRef = useRef(false);
    if (!loading && !error) hasLoadedOnceRef.current = true;

    // Show toast for background refresh errors instead of replacing the view.
    // Gate on hasCachedData (not hasLoadedOnceRef) so the toast is mutually exclusive
    // with the full-page error alert (which shows when error && !hasCachedData).
    // Only clear prevErrorRef when we have a genuine successful state (!error && !loading).
    // During refetch, error is set to null before the request completes, which would
    // otherwise clear prevErrorRef and defeat deduplication when the same error recurs.
    const prevErrorRef = useRef(null);
    useEffect(() => {
        if (error && error !== prevErrorRef.current && hasCachedData) {
            toast.error('Failed to refresh dashboard', {
                description: error,
                duration: 5000,
            });
            prevErrorRef.current = error;
        }
        if (!error && !loading) {
            prevErrorRef.current = null;
        }
    }, [error, loading, hasCachedData]);

    // Auto-refresh: call refetch every 15s when enabled and initial load has completed
    useInterval(refetch, autoRefreshEnabled && hasLoadedOnceRef.current ? REFRESH_INTERVAL_MS : null);

    // Handle individual filter changes
    const handleFilterChange = useCallback((key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    }, []);

    // Stable callback for auto-refresh toggle
    const handleAutoRefreshToggle = useCallback(() => {
        setAutoRefreshEnabled((prev) => !prev);
    }, []);

    // True when we're fetching but already have data to show (date/filter change or auto-refresh)
    const isRefreshing = loading && hasCachedData;

    // Client-side filtering: status filter + milestone filter
    const filteredRoutes = useMemo(() => {
        let result = routes;

        // Status filter (client-side — filters by riskLevel)
        if (filters.statusFilter !== 'all') {
            const riskLevelMap = {
                critical: 'CRITICAL',
                atRisk: 'AT_RISK',
                onTrack: 'ON_TRACK',
            };
            const targetRisk = riskLevelMap[filters.statusFilter];
            if (targetRisk) {
                result = result.filter((r) => r.riskLevel === targetRisk);
            }
        }

        // Milestone filter (client-side — filters by which milestone has work remaining)
        if (filters.milestone !== 'all') {
            result = result.filter((route) => {
                switch (filters.milestone) {
                    case 'labels':
                        return route.labelsCount < route.totalShipments;
                    case 'packing':
                        return route.packedCount < route.labelsCount;
                    case 'loading':
                        return route.transitCount < route.packedCount;
                    case 'complete':
                        return route.transitCount >= route.totalShipments;
                    default:
                        return true;
                }
            });
        }

        return result;
    }, [routes, filters.statusFilter, filters.milestone]);

    // Render loading skeleton (initial load only -- no cached data)
    if (loading && !hasCachedData) {
        return (
            <div className="fulfillment-lifecycle-dashboard p-6 space-y-6">
                <DashboardHeaderSkeleton />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <RouteCardSkeleton key={i} />
                    ))}
                </div>
            </div>
        );
    }

    // Render error state (only when no cached data -- otherwise toast handles it)
    if (error && !hasCachedData) {
        return (
            <div className="fulfillment-lifecycle-dashboard p-6 space-y-6">
                <DashboardHeader facilityName={facilityName} selectedDate={selectedDate}>
                    <AutoRefreshLabel enabled={autoRefreshEnabled} />
                </DashboardHeader>
                <FilterControls
                    selectedDate={selectedDate}
                    onDateChange={setSelectedDate}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                />
                <Alert variant="destructive">
                    <AlertTitle>Error loading dashboard</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            </div>
        );
    }

    // Render empty state
    if (!loading && routes.length === 0) {
        return (
            <div className="fulfillment-lifecycle-dashboard p-6 space-y-6">
                <DashboardHeader facilityName={facilityName} selectedDate={selectedDate}>
                    <AutoRefreshLabel enabled={autoRefreshEnabled} />
                </DashboardHeader>
                <FilterControls
                    selectedDate={selectedDate}
                    onDateChange={setSelectedDate}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                />
                <Alert>
                    <AlertTitle>No routes found</AlertTitle>
                    <AlertDescription>
                        No routes found for {facilityName} on {dayjs(selectedDate).format('MMMM D, YYYY')}.
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="fulfillment-lifecycle-dashboard p-6 space-y-6">
            {/* Header */}
            <DashboardHeader facilityName={facilityName} selectedDate={selectedDate}>
                <AutoRefreshIndicator enabled={autoRefreshEnabled} onToggle={handleAutoRefreshToggle} />
            </DashboardHeader>

            {/* Filter Controls */}
            <FilterControls
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                filters={filters}
                onFilterChange={handleFilterChange}
            />

            {/* Refresh Indicator */}
            {isRefreshing && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Updating dashboard...
                </div>
            )}

            {/* Summary Alerts */}
            <div className={isRefreshing ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
                <SummaryAlerts summary={summary} />
            </div>

            {/* Routes Grid */}
            <div className={isRefreshing ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
                <RoutesGrid routes={filteredRoutes} selectedDate={selectedDate} totalFromApi={routes.length} />
            </div>
        </div>
    );
}

/**
 * Skeleton for dashboard header during loading
 */
function DashboardHeaderSkeleton() {
    return (
        <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
        </div>
    );
}

export default FulfillmentLifecycleDashboard;
